/**
 * DK Sportsbook betslip handoff for prop-based Daily Reveal (Gridiron Draw).
 *
 * The URL itself is built by `buildSportsbookBetslipDeeplink` in sports-config —
 * that is where the per-league DK CMS page mapping lives. This module only
 * decides WHICH selection ids belong in the betslip and how the link is opened.
 */

import {
  normalizeSportsbookSelectionIds,
  type SportsbookBetslipTargetName,
} from '@low6-pickem/sports-config';
import type { PropCardMeta } from '@/types/reveal';

/**
 * Master switch for the whole Sportsbook betslip handoff — both the once-per-day
 * prompt modal and the persistent "Add to Bet Slip" CTA on the pick card.
 *
 * Temporarily `false`: DK are changing the betslip link, so the handoff is hidden
 * rather than removed. Flip back to `true` once the new link is confirmed — the
 * components, hook and tests are all intact behind it.
 */
export const SPORTSBOOK_BETSLIP_ENABLED = false;

/**
 * Which DK Sportsbook estate this build's deeplinks point at.
 *
 * DK supplied a different path shape for preprod after the production CMS-page form
 * failed to resolve on stage, so the two environments genuinely need different URLs.
 *
 * Keyed off the Vite build mode, which the deploy workflows already set per
 * environment (`--mode staging` on stage, `--mode production` on prod). Anything else
 * — local `vite`, the Azure dev build — gets production links, which is the
 * instruction for every environment DK has not given us a separate page for.
 *
 * `VITE_SPORTSBOOK_BETSLIP_TARGET` forces a target regardless of mode. That exists
 * because which form works where is still unsettled: it lets QA try the other shape
 * on an existing build instead of waiting on a code change and redeploy.
 */
export function resolveSportsbookBetslipTarget(): SportsbookBetslipTargetName {
  const override = import.meta.env.VITE_SPORTSBOOK_BETSLIP_TARGET;
  if (override === 'staging' || override === 'production') return override;
  return import.meta.env.MODE === 'staging' ? 'staging' : 'production';
}

type EntryCardLike = {
  isSelected: boolean;
  prop?: Pick<PropCardMeta, 'sportsbookSelectionId'> | null;
};

/**
 * Sportsbook selection ids for the outcomes the player actually submitted in
 * this entry.
 *
 * A Gridiron Draw entry reveals several props but only the selected one is the
 * player's pick, so only selected cards contribute. The unpicked cards in the
 * same pack, and props from any other day or contest, are never included — the
 * betslip has to mirror the entry, not the day's market list.
 *
 * Prop markets are frozen on the "over"/eligible side only (see
 * `SportsbookFeedPropMarketProvider`), so each id is already the exact outcome
 * the player backed; the opposite side is never appended.
 */
export function getEntrySportsbookSelectionIds(
  cards: readonly EntryCardLike[] | null | undefined
): string[] {
  if (!cards) return [];
  return normalizeSportsbookSelectionIds(
    cards.filter(card => card.isSelected).map(card => card.prop?.sportsbookSelectionId)
  );
}

/**
 * Sportsbook selection ids for every prop revealed to the player today, picked or not.
 *
 * Only for the How to Play test link, which has to be able to hand SOMETHING to the
 * sportsbook before a pick is submitted — otherwise anyone testing the production link
 * has to make a selection first, which is not always possible mid-week. The real CTA and
 * the post-pick prompt keep using {@link getEntrySportsbookSelectionIds}: a bet slip the
 * player sees off the back of their own pick must mirror the entry exactly.
 */
export function getRevealedSportsbookSelectionIds(
  cards: readonly Pick<EntryCardLike, 'prop'>[] | null | undefined
): string[] {
  if (!cards) return [];
  return normalizeSportsbookSelectionIds(cards.map(card => card.prop?.sportsbookSelectionId));
}

/**
 * How long to let the OS claim `dksb://` before assuming nothing will (DKPLAT-778).
 *
 * Long enough that a cold app launch still wins the race, short enough that a player
 * without the app is not left staring at a dead tap.
 */
export const SPORTSBOOK_APP_HANDOFF_GRACE_MS = 1_200;

/**
 * Navigate the OUTERMOST frame, not our own.
 *
 * Daily Reveal always runs inside a frame DK owns — the lobby iframe on web, and the
 * DK app's webview container in native. A navigation scoped to our own frame is
 * invisible to the layer that has to act on it: the native app inspects main-frame
 * navigations to decide what to route to a native view, and the web sportsbook refuses
 * to be iframed at all. Both failures look like "the link does nothing".
 *
 * Writing `location.href` on a cross-origin `window.top` is permitted (it is reading
 * that throws), which is why the lobby already breaks out this way for its external
 * sportsbook cards. The catch is belt-and-braces for a container that blocks even the
 * write — a same-frame navigation is still better than a dead tap.
 */
export function navigateTopFrame(url: string): void {
  if (!url) return;
  try {
    const top = window.top;
    if (top && top !== window) {
      top.location.href = url;
      return;
    }
  } catch {
    // Container refused the top-frame write — fall through to our own frame.
  }
  window.location.href = url;
}

/**
 * Whether Daily Reveal is running inside DK's native app webview rather than a browser.
 *
 * Only the user agent can tell: both contexts frame us, and neither passes safe-area
 * insets through. Android WebViews carry `; wv`; iOS WKWebView omits the `Safari/` token
 * every real iOS browser sends. Same test as Squares' and Pick'em's `isDkNativeEmbed`.
 */
export function isDkNativeApp(
  userAgent: string = typeof navigator === 'undefined' ? '' : navigator.userAgent
): boolean {
  if (/Android/.test(userAgent)) return /;\s*wv\b/.test(userAgent);
  if (/iPhone|iPad|iPod/.test(userAgent)) return !/Safari\//.test(userAgent);
  return false;
}

/**
 * Fall back to the web sportsbook if the DK Sportsbook app never takes the handoff.
 *
 * Call this on the tap WITHOUT preventing the anchor's default action: the anchor
 * itself offers `dksb://` to the OS, which is the only way to reach the app without
 * unloading the SPA. This just watches what happens next.
 *
 * If the app opens, the page is backgrounded — `visibilitychange`/`pagehide` fire and
 * the fallback is cancelled. If nothing claims the scheme, the page is still visible
 * when the timer fires and we navigate to DK's web sportsbook. That navigation does
 * leave the SPA, which is correct here: unlike the original bug, this one has a real
 * destination on the other side.
 *
 * Never armed inside DK's native app. The app routes `dksb://` itself, often without
 * backgrounding our webview, so "still visible" does not mean "nothing claimed it" — and
 * the web sportsbook, opened inside the app, only shows a "download the app" page.
 *
 * Returns a cancel function so a caller unmounting mid-flight can drop the fallback.
 */
export function scheduleSportsbookWebFallback(
  webUrl: string | null | undefined,
  graceMs: number = SPORTSBOOK_APP_HANDOFF_GRACE_MS
): () => void {
  if (!webUrl || isDkNativeApp()) return () => {};

  let cancelled = false;

  const timer = window.setTimeout(() => {
    // Backgrounded without an event we caught — the app almost certainly took it.
    if (cancelled || document.visibilityState === 'hidden') return;
    cancel();
    navigateTopFrame(webUrl);
  }, graceMs);

  function cancel() {
    if (cancelled) return;
    cancelled = true;
    window.clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', cancel);
  }

  function onVisibilityChange() {
    if (document.visibilityState === 'hidden') cancel();
  }

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', cancel);

  return cancel;
}
